import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchMarketCompetition, updateMarketCompetition } from "../../../store/startup/market/marketSlice";
import {
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Switch,
    Modal,
    Backdrop,
    Fade,
    RadioGroup,
    FormControlLabel,
    Radio,
    OutlinedInput,
    Grid2,
    useTheme,
    alpha
} from "@mui/material";
import { PhotoCamera, Close } from "@mui/icons-material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SingleFileUpload from "../../fileUpload/FileUpload";
import OtherTopics from "./marketCompetition/OtherTopics";

const NadirMarket = () => {
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const { marketCompetition, loading, error } = useSelector((state) => state.market);
    const [marketInfo, setMarketInfo] = useState("");
    const [competitionInfo, setCompetitionInfo] = useState("");
    const [targetAudienceInfo, setTargetAudienceInfo] = useState("");
    const [commercializationPlan, setCommercializationPlan] = useState("");
    const [marketDocumentPath, setMarketDocumentPath] = useState("");
    const [competitionDocumentPath, setCompetitionDocumentPath] = useState("");
    const [targetAudienceDocumentPath, setTargetAudienceDocumentPath] = useState("");
    const [commercializationDocumentPath, setCommercializationDocumentPath] = useState("");
    const [errors, setErrors] = useState({
        marketInfo: false,
        competitionInfo: false,
        targetAudienceInfo: false,
        commercializationPlan: false,
        marketDocumentPath: false,
        competitionDocumentPath: false,
        targetAudienceDocumentPath: false,
        commercializationDocumentPath: false
    });

    const theme = useTheme();
    
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['clean']
        ]
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet'
    ];

    useEffect(() => {
        if (startupId) {
            dispatch(fetchMarketCompetition(startupId));
        }
    }, [dispatch, startupId]);

    useEffect(() => {
        if (marketCompetition) {
            console.log("Market Competition Data:", marketCompetition);
            setMarketInfo(marketCompetition.marketInfo || "");
            setCompetitionInfo(marketCompetition.competitionInfo || "");
            setTargetAudienceInfo(marketCompetition.targetAudienceInfo || "");
            setCommercializationPlan(marketCompetition.commercializationPlan || "");
            
            // Dosya yollarını set et
            setMarketDocumentPath(marketCompetition.marketDocumentPath || "");
            setCompetitionDocumentPath(marketCompetition.competitionDocumentPath || "");
            setTargetAudienceDocumentPath(marketCompetition.targetAudienceDocumentPath || "");
            setCommercializationDocumentPath(marketCompetition.commercializationDocumentPath || "");
            
            // Debug için log
            console.log("Document Paths:", {
                marketDocumentPath: marketCompetition.marketDocumentPath,
                competitionDocumentPath: marketCompetition.competitionDocumentPath,
                targetAudienceDocumentPath: marketCompetition.targetAudienceDocumentPath,
                commercializationDocumentPath: marketCompetition.commercializationDocumentPath
            });
        }
    }, [marketCompetition]);

    const handleSave = async () => {
        setErrors({
            marketInfo: false,
            competitionInfo: false,
            targetAudienceInfo: false,
            commercializationPlan: false,
            marketDocumentPath: false,
            competitionDocumentPath: false,
            targetAudienceDocumentPath: false,
            commercializationDocumentPath: false
        });

        const newErrors = {
            marketInfo: !marketInfo.trim(),
            competitionInfo: !competitionInfo.trim(),
            targetAudienceInfo: !targetAudienceInfo.trim(),
            commercializationPlan: !commercializationPlan.trim(),
            //marketDocumentPath: !marketDocumentPath,
            //competitionDocumentPath: !competitionDocumentPath,
            //targetAudienceDocumentPath: !targetAudienceDocumentPath,
            //commercializationDocumentPath: !commercializationDocumentPath
        };

        if (Object.values(newErrors).some(error => error)) {
            setErrors(newErrors);
            return;
        }

        const data = {
            startupId,
            marketInfo,
            competitionInfo,
            targetAudienceInfo,
            commercializationPlan,
            marketDocumentPath,
            competitionDocumentPath,
            targetAudienceDocumentPath,
            commercializationDocumentPath
        };

        try {
            await dispatch(updateMarketCompetition(data)).unwrap();
        } catch (error) {
            console.error("Failed to update market competition:", error);
        }
    };

    return (
        <Box sx={{ 
            margin: "0 auto", 
            maxWidth: 900,
            '& .quill': {
                '& .ql-toolbar': {
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderTopRightRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.mode === 'light' 
                        ? alpha(theme.palette.grey[100], 0.5)
                        : alpha(theme.palette.grey[800], 0.5),
                    '& .ql-stroke': {
                        stroke: theme.palette.mode === 'light' 
                            ? theme.palette.grey[700]
                            : theme.palette.grey[300]
                    },
                    '& .ql-fill': {
                        fill: theme.palette.mode === 'light' 
                            ? theme.palette.grey[700]
                            : theme.palette.grey[300]
                    },
                    '& .ql-picker': {
                        color: theme.palette.text.primary
                    },
                    '& .ql-picker-options': {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                        boxShadow: theme.shadows[3]
                    }
                },
                '& .ql-container': {
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.background.paper
                },
                '& .ql-editor': {
                    minHeight: '200px',
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.body1.fontSize,
                    color: theme.palette.text.primary,
                    '&.ql-blank::before': {
                        color: theme.palette.text.disabled,
                        fontStyle: 'normal',
                        content: 'attr(data-placeholder)'
                    }
                }
            }
        }}>
            <Typography variant="h5" gutterBottom>
                Market ve Rekabet Hakkında
            </Typography>
            <Divider sx={{ marginBottom: 3, bgcolor: 'black' }} />
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 8 }}>
                    <Box sx={{ mb: 1, width: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Proje Hakkında Özet Bilgi
                        </Typography>
                        <ReactQuill 
                            theme="snow" 
                            value={marketInfo} 
                            onChange={setMarketInfo} 
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Proje hakkında özet bilgi giriniz..."
                        />
                        {errors.marketInfo && (
                            <Typography color="error" variant="caption">
                                Proje hakkında bilgi boş bırakılamaz
                            </Typography>
                        )}
                    </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }} >
                        <SingleFileUpload 
                            onUploadComplete={(path) => setMarketDocumentPath(path)}
                            existFilePath={marketDocumentPath}
                            fileUploadPath="startup"
                        />
                        {errors.marketDocumentPath && (
                            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                Lütfen bir dosya yükleyin
                            </Typography>
                        )}
                    </Box>
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 8 }}>
                    <Box sx={{ mb: 1, width: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Rekabet Hakkında Özet Bilgi
                        </Typography>
                        <ReactQuill 
                            theme="snow" 
                            value={competitionInfo} 
                            onChange={setCompetitionInfo}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Rekabet hakkında özet bilgi giriniz..."
                        />
                        {errors.competitionInfo && (
                            <Typography color="error" variant="caption">
                                Rekabet hakkında bilgi boş bırakılamaz
                            </Typography>
                        )}
                    </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }} >
                        <SingleFileUpload 
                            onUploadComplete={(path) => setCompetitionDocumentPath(path)}
                            existFilePath={competitionDocumentPath}
                            fileUploadPath="startup"
                        />
                        {errors.competitionDocumentPath && (
                            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                Lütfen bir dosya yükleyin
                            </Typography>
                        )}
                    </Box>
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 8 }}>
                    <Box sx={{ mb: 1, width: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Hedef Kitle Hakkında Özet Bilgi
                        </Typography>
                        <ReactQuill 
                            theme="snow" 
                            value={targetAudienceInfo} 
                            onChange={setTargetAudienceInfo}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Hedef kitle hakkında özet bilgi giriniz..."
                        />
                        {errors.targetAudienceInfo && (
                            <Typography color="error" variant="caption">
                                Hedef kitle hakkında bilgi boş bırakılamaz
                            </Typography>
                        )}
                    </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }} >
                        <SingleFileUpload 
                            onUploadComplete={(path) => setTargetAudienceDocumentPath(path)}
                            existFilePath={targetAudienceDocumentPath}
                            fileUploadPath="startup"
                        />
                        {errors.targetAudienceDocumentPath && (
                            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                Lütfen bir dosya yükleyin
                            </Typography>
                        )}
                    </Box>
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 8 }}>
                    <Box sx={{ mb: 1, width: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Ticarileşme Planı ve İş Modeli
                        </Typography>
                        <ReactQuill 
                            theme="snow" 
                            value={commercializationPlan} 
                            onChange={setCommercializationPlan}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Ticarileşme planı ve iş modeli giriniz..."
                        />
                        {errors.commercializationPlan && (
                            <Typography color="error" variant="caption">
                                Ticarileşme planı boş bırakılamaz
                            </Typography>
                        )}
                    </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }} >
                        <SingleFileUpload 
                            onUploadComplete={(path) => setCommercializationDocumentPath(path)}
                            existFilePath={commercializationDocumentPath}
                            fileUploadPath="startup"
                        />
                        {errors.commercializationDocumentPath && (
                            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                Lütfen bir dosya yükleyin
                            </Typography>
                        )}
                    </Box>
                </Grid2>
            </Grid2>

            <Divider sx={{ marginBottom: 3 }} />

            <Box sx={{ mt: 4, p: 2, borderRadius: 1 }}>
                <OtherTopics />
            </Box>
            
            <Box sx={{ marginTop: 4, textAlign: "right" }}>
                <Button color="primary" variant="contained" onClick={handleSave}>Kaydet</Button>
            </Box>

        </Box>
    );
};

export default NadirMarket;
